import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import '../styles/AddProject.css';
import '../../ProfilePages/styles/QuotationForm.css';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  emptyServiceQuote,
  newPricedTask,
  quoteTotal,
  toServiceLinesPayload,
} from '../../../utils/servicePricing';
import ServicePricingFields from '../../../components/ServicePricingFields';
import {
  htmlToPlainText,
  mapDisplayToDbStatus,
  mapStatusToDisplay,
} from '../utils/projectUtils';
import {
  PROJECT_FREQUENCY_OPTIONS,
  matchProjectFrequencyOption,
  isRecurringFrequencyOption,
} from '../../../utils/projectFrequency';

const FIND_ONE = `
  query FindOneProject($id: String!) {
    findOneProject(id: $id) {
      id
      projectId
      name
      startDate
      deadLine
      deadlineOngoing
      startTime
      endTime
      status
      recurrenceFrequency
      seriesRootId
      billingCadence
      autoSendMonthlyInvoice
      invoiceLineMode
      projectServices
      quotedServices
      purchaseOrderNumber
      projectAddress
      deliveryAddress
      attention
      booking {
        id
        fullName
        email
        estimatedPRice
        message
        bookingServices {
          id
          service {
            id
            service
          }
        }
      }
      manager {
        id
      }
    }
  }
`;

const GET_SERVICES = `
  query GetService {
    getService(includeStats: false) {
      id
      service
      description
    }
  }
`;

const FIND_BOOKING_SERVICE = `
  query FindBookingService($bookingId: String!) {
    findBookingService(bookingId: $bookingId) {
      data {
        id
        price
        total
        service {
          id
          service
        }
      }
    }
  }
`;

const CREATE_BOOKING_SERVICES = `
  mutation CreateBookingServices($data: CreateBookingServicesInput!) {
    createBookingServices(data: $data) {
      id
    }
  }
`;

function quotesFromProject(project) {
  const next = {};
  for (const row of project?.projectServices || []) {
    const id = String(row?.service?.id || '').trim();
    if (!id) continue;
    const tasks =
      Array.isArray(row.tasks) && row.tasks.length
        ? row.tasks.map((task) => ({
            // Keep scope, group, instructions etc. so a save doesn't drop them.
            ...task,
            id: task.id || newPricedTask().id,
            title: task.title || '',
            description: task.description || '',
            price: String(task.price ?? ''),
            discount: String(task.discount ?? '0'),
          }))
        : [newPricedTask('')];
    next[id] = {
      linePricingMode:
        String(row.linePricingMode || row.pricingMode || '').toLowerCase() ===
        'task'
          ? 'task'
          : 'service',
      price: String(row.price ?? row.total ?? ''),
      discount: '0',
      tasks,
    };
  }
  return next;
}

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const serviceIdsFromProject = (project) => {
  const fromProject = Array.isArray(project?.projectServices)
    ? project.projectServices
    : [];
  if (fromProject.length) {
    return fromProject.map((row) => row?.service?.id).filter(Boolean);
  }
  const fromQuoted = Array.isArray(project?.quotedServices)
    ? project.quotedServices
    : [];
  if (fromQuoted.length) {
    return fromQuoted.map((row) => row?.service?.id).filter(Boolean);
  }
  const fromBooking = project?.booking?.bookingServices || [];
  return fromBooking.map((row) => row?.service?.id).filter(Boolean);
};

export default function EditProject({ projectId, onClose, onSaved }) {
  const [formData, setFormData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [managers, setManagers] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [bookingServicePrices, setBookingServicePrices] = useState({});
  const [serviceQuotes, setServiceQuotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingServices, setLoadingServices] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [projectRes, bookingsRes, managersRes, servicesRes] =
          await Promise.all([
            graphqlRequest(FIND_ONE, { id: projectId }),
            graphqlRequest(
              `query FindAllBooking($page: Float!, $limit: Float!) {
              findAllBooking(page: $page, limit: $limit, includeSignUrls: false) {
                data { id fullName email estimatedPRice }
              }
            }`,
              { page: 1, limit: 500 },
            ),
            graphqlRequest(
              `query GetAllUser($page: Int, $limit: Int, $roles: [String!]) {
              getAllUser(page: $page, limit: $limit, roles: $roles) {
                data { id firstName lastName email }
              }
            }`,
              {
                page: 1,
                limit: 100,
                roles: ['OPERATIONS_MANAGER', 'SUPERVISOR', 'ADMIN'],
              },
            ),
            graphqlRequest(GET_SERVICES).catch(() => ({})),
          ]);

        const project = projectRes?.findOneProject;
        if (!project) {
          toast.error('Project not found');
          onClose();
          return;
        }

        const bookingList = bookingsRes?.findAllBooking?.data || [];
        const linkedBooking = project.booking;
        // Ensure the project's client appears in the dropdown even if not on page 1
        // of findAllBooking (or filtered out of that list).
        if (
          linkedBooking?.id &&
          !bookingList.some((b) => b.id === linkedBooking.id)
        ) {
          bookingList.unshift({
            id: linkedBooking.id,
            fullName: linkedBooking.fullName || 'Linked client',
            email: linkedBooking.email || '',
            estimatedPRice: linkedBooking.estimatedPRice,
          });
        }
        setBookings(bookingList);
        setManagers(managersRes?.getAllUser?.data || []);
        setServices(
          Array.isArray(servicesRes?.getService) ? servicesRes.getService : [],
        );
        setSelectedServiceIds(serviceIdsFromProject(project));
        setServiceQuotes(quotesFromProject(project));
        setFormData({
          projectTitle: project.name || '',
          bookingId: linkedBooking?.id || '',
          clientName: linkedBooking?.fullName || '',
          price: linkedBooking?.estimatedPRice || '',
          startDate: formatDateInput(project.startDate),
          endDate: formatDateInput(project.deadLine),
          deadlineOngoing: Boolean(project.deadlineOngoing),
          startTime: project.startTime || '',
          endTime: project.endTime || '',
          status: mapStatusToDisplay(project.status),
          description: htmlToPlainText(linkedBooking?.message || ''),
          manager: project.manager?.id || '',
          purchaseOrderNumber: project.purchaseOrderNumber || '',
          projectAddress: project.projectAddress || '',
          deliveryAddress: project.deliveryAddress || '',
          attention: project.attention || '',
          frequency: matchProjectFrequencyOption(project.recurrenceFrequency),
          seriesRootId: project.seriesRootId || null,
          billingCadence:
            String(project.billingCadence || '').toLowerCase() === 'monthly'
              ? 'monthly'
              : 'per_visit',
          autoSendMonthlyInvoice: Boolean(project.autoSendMonthlyInvoice),
          invoiceLineMode:
            String(project.invoiceLineMode || '').toLowerCase() === 'task'
              ? 'task'
              : 'service',
        });

        const bookingId = linkedBooking?.id;
        if (bookingId) {
          setLoadingServices(true);
          try {
            const bookingSvc = await graphqlRequest(FIND_BOOKING_SERVICE, {
              bookingId,
            });
            const rows = bookingSvc?.findBookingService?.data || [];
            const priceMap = {};
            rows.forEach((row) => {
              const sid = row?.service?.id;
              if (!sid) return;
              priceMap[sid] = Number(row.total ?? row.price) || 0;
            });
            setBookingServicePrices(priceMap);
          } catch (err) {
            console.error('Error fetching booking services:', err);
          } finally {
            setLoadingServices(false);
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(err?.message || 'Failed to load project');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    if (projectId) load();
  }, [projectId, onClose]);

  const loadBookingServicePrices = async (bookingId) => {
    if (!bookingId) {
      setBookingServicePrices({});
      return;
    }
    setLoadingServices(true);
    try {
      const bookingSvc = await graphqlRequest(FIND_BOOKING_SERVICE, {
        bookingId,
      });
      const rows = bookingSvc?.findBookingService?.data || [];
      const priceMap = {};
      rows.forEach((row) => {
        const sid = row?.service?.id;
        if (!sid) return;
        priceMap[sid] = Number(row.total ?? row.price) || 0;
      });
      setBookingServicePrices(priceMap);
    } catch (err) {
      console.error('Error fetching booking services:', err);
      setBookingServicePrices({});
    } finally {
      setLoadingServices(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [name]: value };
      if (name === 'bookingId') {
        const booking = bookings.find((b) => b.id === value);
        next.price = booking?.estimatedPRice || prev.price;
        next.clientName = booking?.fullName || '';
        next.description =
          value === prev.bookingId
            ? prev.description
            : htmlToPlainText(booking?.message || prev.description);
      }
      return next;
    });
    if (name === 'bookingId') {
      loadBookingServicePrices(value);
    }
  };

  const isServiceSelected = (serviceId) =>
    selectedServiceIds.includes(serviceId);

  const toggleService = (serviceId) => {
    setSelectedServiceIds((prev) => {
      if (prev.includes(serviceId)) {
        setServiceQuotes((quotes) => {
          const next = { ...quotes };
          delete next[serviceId];
          return next;
        });
        return prev.filter((id) => id !== serviceId);
      }
      setServiceQuotes((quotes) => ({
        ...quotes,
        [serviceId]:
          quotes[serviceId] ||
          emptyServiceQuote(bookingServicePrices[serviceId] || 0),
      }));
      return [...prev, serviceId];
    });
  };

  const selectedTotal = selectedServiceIds.reduce(
    (sum, id) =>
      sum + quoteTotal(serviceQuotes[id], bookingServicePrices[id] || 0),
    0,
  );

  const saveBookingServices = async (bookingId) => {
    if (!bookingId || !selectedServiceIds.length) return;

    const existing = await graphqlRequest(FIND_BOOKING_SERVICE, { bookingId });
    const existingRows = existing?.findBookingService?.data || [];
    const existingIds = new Set(
      existingRows.map((row) => row?.service?.id).filter(Boolean),
    );

    const toCreate = selectedServiceIds
      .filter((id) => !existingIds.has(id))
      .map((id) => {
        const priceNum =
          Number(serviceQuotes[id]?.price) ||
          Number(bookingServicePrices[id]) ||
          0;
        return {
          booking: bookingId,
          service: id,
          price: priceNum,
          discount: 0,
          total: priceNum,
        };
      });

    if (toCreate.length) {
      await graphqlRequest(CREATE_BOOKING_SERVICES, {
        data: { input: toCreate },
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData?.projectTitle?.trim()) {
      toast.error('Please enter a project title');
      return;
    }
    if (!formData.bookingId) {
      toast.error('Please select a client');
      return;
    }
    if (!selectedServiceIds.length) {
      toast.error('Please select at least one service');
      return;
    }

    try {
      setSubmitting(true);

      await saveBookingServices(formData.bookingId).catch((err) => {
        console.error('Error saving booking services:', err);
        throw err;
      });

      await graphqlRequest(
        `mutation UpdateProject($input: UpdateProjectInput!) {
          updateProject(updateProjectInput: $input) {
            id
            name
            status
            startDate
            nextVisitAt
            recurrenceActive
            projectServices
          }
        }`,
        {
          input: {
            id: projectId,
            bookingId: formData.bookingId,
            projectName: formData.projectTitle.trim(),
            startDate: formData.startDate || null,
            deadLine: formData.deadlineOngoing ? null : formData.endDate || null,
            deadlineOngoing: Boolean(formData.deadlineOngoing),
            startTime: formData.startTime || null,
            endTime: formData.endTime || null,
            status: mapDisplayToDbStatus(formData.status),
            manager: formData.manager || null,
            serviceIds: selectedServiceIds,
            serviceLines: toServiceLinesPayload(
              selectedServiceIds,
              serviceQuotes,
              services,
              formData.frequency,
            ),
            frequency: formData.frequency || 'One-time',
            billingCadence:
              !formData.seriesRootId &&
              isRecurringFrequencyOption(formData.frequency)
                ? formData.billingCadence || 'per_visit'
                : 'per_visit',
            invoiceLineMode:
              !formData.seriesRootId &&
              isRecurringFrequencyOption(formData.frequency) &&
              formData.billingCadence === 'monthly'
                ? formData.invoiceLineMode || 'service'
                : 'service',
            autoSendMonthlyInvoice:
              !formData.seriesRootId &&
              isRecurringFrequencyOption(formData.frequency) &&
              formData.billingCadence === 'monthly'
                ? Boolean(formData.autoSendMonthlyInvoice)
                : false,
            purchaseOrderNumber: formData.purchaseOrderNumber.trim() || null,
            projectAddress: formData.projectAddress.trim() || null,
            deliveryAddress: formData.deliveryAddress.trim() || null,
            attention: formData.attention.trim() || null,
          },
        },
      );

      if (formData.bookingId) {
        const priceToSave =
          selectedTotal > 0 ? String(selectedTotal) : formData.price || null;
        await graphqlRequest(
          `mutation UpdateBooking($input: UpdateBookingInput!) {
            updateBooking(updateBookingInput: $input) {
              id
            }
          }`,
          {
            input: {
              id: formData.bookingId,
              estimatedPRice: priceToSave,
              message: htmlToPlainText(formData.description) || null,
            },
          },
        );
      }

      toast.success('Project updated successfully');
      onSaved?.();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to update project');
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div className="overlay-project" onClick={onClose} role="presentation">
      <div
        className="project-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-project-title"
      >
        <div className="modal-header-project">
          <h2 id="edit-project-title">Edit Project</h2>
          <button
            type="button"
            className="close-btn-project"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {loading || !formData ? (
          <div className="project-form-body">
            <p className="form-hint">Loading project...</p>
          </div>
        ) : (
          <form className="project-form" onSubmit={handleSubmit}>
            <div className="project-form-body">
              <div className="form-group-project">
                <label htmlFor="edit-projectTitle">Project Title</label>
                <input
                  id="edit-projectTitle"
                  type="text"
                  name="projectTitle"
                  value={formData.projectTitle}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row-project">
                <div className="form-group-project">
                  <label htmlFor="edit-bookingId">Client</label>
                  <select
                    id="edit-bookingId"
                    name="bookingId"
                    value={formData.bookingId}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select client</option>
                    {bookings.map((booking) => (
                      <option key={booking.id} value={booking.id}>
                        {booking.fullName}
                        {booking.email ? ` — ${booking.email}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="form-hint">
                    {formData.bookingId
                      ? 'You can change the linked client for this project.'
                      : 'No client linked yet — select a lead/client to attach.'}
                  </p>
                  {bookings.length === 0 && (
                    <p className="form-hint">
                      No leads found. Add a lead first, then link it here.
                    </p>
                  )}
                </div>
                <div className="form-group-project">
                  <label htmlFor="edit-price">Price</label>
                  <input
                    id="edit-price"
                    type="text"
                    name="price"
                    value={
                      selectedTotal > 0
                        ? selectedTotal.toFixed(2)
                        : formData.price
                    }
                    onChange={handleChange}
                    readOnly={selectedTotal > 0}
                  />
                </div>
              </div>

              <div className="form-group-project">
                <label htmlFor="edit-attention">Attention (optional)</label>
                <input
                  id="edit-attention"
                  type="text"
                  name="attention"
                  value={formData.attention}
                  onChange={handleChange}
                  placeholder="Client contact attending the site"
                />
                <p className="form-hint">
                  Who from the client side is attending / should be asked for on
                  site.
                </p>
              </div>

              <div className="form-group-project">
                <label htmlFor="edit-purchaseOrderNumber">
                  Purchase Order Number (optional)
                </label>
                <input
                  id="edit-purchaseOrderNumber"
                  type="text"
                  name="purchaseOrderNumber"
                  value={formData.purchaseOrderNumber}
                  onChange={handleChange}
                  placeholder="PO number for invoice"
                />
              </div>

              <div className="form-group-project">
                <label htmlFor="edit-projectAddress">
                  Project Address (optional)
                </label>
                <input
                  id="edit-projectAddress"
                  type="text"
                  name="projectAddress"
                  value={formData.projectAddress}
                  onChange={handleChange}
                  placeholder="Where the work is done (site address)"
                />
                <p className="form-hint">
                  Used as the project location. Falls back to client address if
                  empty.
                </p>
              </div>

              <div className="form-group-project">
                <label htmlFor="edit-deliveryAddress">
                  Delivery Address (optional)
                </label>
                <input
                  id="edit-deliveryAddress"
                  type="text"
                  name="deliveryAddress"
                  value={formData.deliveryAddress}
                  onChange={handleChange}
                  placeholder="Where to deliver materials / documents"
                />
              </div>

              <div className="form-group-project">
                <label>Services</label>
                <p className="form-hint">
                  Change services for this project. Prices come from the lead
                  Services section when available.
                </p>
                {loadingServices ? (
                  <p className="form-hint">Loading services...</p>
                ) : services.length === 0 ? (
                  <p className="form-hint">
                    No services found. Add a service first.
                  </p>
                ) : (
                  <div className="project-services-list">
                    {services.map((svc) => {
                      const selected = isServiceSelected(svc.id);
                      const knownPrice = bookingServicePrices[svc.id];
                      return (
                        <div key={svc.id} className="project-service-row">
                          <label className="project-service-check">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleService(svc.id)}
                            />
                            <span>{svc.service}</span>
                          </label>
                          {knownPrice != null && Number(knownPrice) > 0 ? (
                            <span className="project-service-price-ro">
                              {Number(knownPrice).toFixed(2)}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
                {selectedServiceIds.length > 0 && (
                  <>
                    {selectedServiceIds.map((id) => {
                      const svc = services.find((row) => row.id === id);
                      return (
                        <div key={id} className="pm-selected-pricing-card">
                          <strong>{svc?.service || 'Service'}</strong>
                          <ServicePricingFields
                            serviceName={svc?.service}
                            quote={serviceQuotes[id]}
                            onChange={(next) =>
                              setServiceQuotes((prev) => ({
                                ...prev,
                                [id]: next,
                              }))
                            }
                          />
                        </div>
                      );
                    })}
                  <p className="form-hint project-services-total">
                    Selected: {selectedServiceIds.length}
                    {selectedTotal > 0
                      ? ` · Total: ${selectedTotal.toFixed(2)}`
                      : ''}
                  </p>
                  </>
                )}
              </div>

              <div className="form-row-project">
                <div className="form-group-project">
                  <label htmlFor="edit-startDate">Start Date</label>
                  <input
                    id="edit-startDate"
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group-project">
                  <label htmlFor="edit-endDate">End Date</label>
                  {formData.deadlineOngoing ? (
                    <input id="edit-endDate" type="text" value="Ongoing" readOnly />
                  ) : (
                    <input
                      id="edit-endDate"
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                    />
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontWeight: 400 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(formData.deadlineOngoing)}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, deadlineOngoing: e.target.checked }))
                      }
                      style={{ width: 'auto' }}
                    />
                    Ongoing (until the client stops)
                  </label>
                </div>
              </div>

              <div className="form-group-project">
                <label htmlFor="edit-frequency">Service frequency</label>
                <select
                  id="edit-frequency"
                  name="frequency"
                  value={formData.frequency || 'One-time'}
                  onChange={handleChange}
                >
                  {PROJECT_FREQUENCY_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <p className="form-hint">
                  Cadence for this project. Recurring visits follow Daily,
                  Weekly, Fortnightly, or Monthly.
                </p>
              </div>

              {!formData.seriesRootId &&
              isRecurringFrequencyOption(formData.frequency) ? (
                <>
                  <div className="form-group-project">
                    <label htmlFor="edit-billingCadence">Invoice timing</label>
                    <select
                      id="edit-billingCadence"
                      name="billingCadence"
                      value={formData.billingCadence || 'per_visit'}
                      onChange={handleChange}
                    >
                      <option value="per_visit">
                        Each visit (current default)
                      </option>
                      <option value="monthly">
                        Monthly (end of month, contract)
                      </option>
                    </select>
                    <p className="form-hint">
                      Monthly keeps visits for rostering but bills once per
                      month. Set End Date to the contract end.
                    </p>
                  </div>
                  {formData.billingCadence === 'monthly' ? (
                    <>
                      <div className="form-group-project">
                        <label htmlFor="edit-invoiceLineMode">
                          Monthly invoice lines
                        </label>
                        <select
                          id="edit-invoiceLineMode"
                          name="invoiceLineMode"
                          value={formData.invoiceLineMode || 'service'}
                          onChange={handleChange}
                        >
                          <option value="service">Service package</option>
                          <option value="task">
                            Tasks completed / priced
                          </option>
                        </select>
                      </div>
                      <label
                        className="project-checkbox-row"
                        htmlFor="edit-autoSendMonthlyInvoice"
                      >
                        <input
                          id="edit-autoSendMonthlyInvoice"
                          type="checkbox"
                          name="autoSendMonthlyInvoice"
                          checked={Boolean(formData.autoSendMonthlyInvoice)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              autoSendMonthlyInvoice: e.target.checked,
                            }))
                          }
                        />
                        <span className="project-checkbox-copy">
                          <strong>Auto-send the monthly invoice</strong>
                          <span>
                            Leave off to create a draft at month end for you to
                            send.
                          </span>
                        </span>
                      </label>
                    </>
                  ) : null}
                </>
              ) : null}

              <div className="form-row-project">
                <div className="form-group-project">
                  <label htmlFor="edit-startTime">Start Time</label>
                  <input
                    id="edit-startTime"
                    type="time"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group-project">
                  <label htmlFor="edit-endTime">End Time</label>
                  <input
                    id="edit-endTime"
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row-project">
                <div className="form-group-project">
                  <label htmlFor="edit-status">Status</label>
                  <select
                    id="edit-status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Active">Active</option>
                    <option value="Complete">Complete</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                  <p className="form-hint">
                    Set to Active to make this project visible to ops. Inactive
                    is used when that project’s quotation was rejected.
                  </p>
                </div>
                <div className="form-group-project">
                  <label htmlFor="edit-manager">Assign Manager</label>
                  <select
                    id="edit-manager"
                    name="manager"
                    value={formData.manager}
                    onChange={handleChange}
                  >
                    <option value="">Unassigned</option>
                    {managers.map((m) => {
                      const fullName =
                        `${m.firstName || ''} ${m.lastName || ''}`.trim();
                      return (
                        <option key={m.id} value={m.id}>
                          {fullName || m.email}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="form-group-project">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter project description"
                />
              </div>
            </div>

            <div className="save-btn-container">
              <button
                type="button"
                className="cancel-btn-project"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="save-btn" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
